/**
 * 主题星座的连线（design §11.10）：同一主题的各站（日子）按时间先后连成直线段，切过螺线，和顺着螺线走的星轨一眼分得开。
 * 淡金色虚线约 1.4 像素宽，外面一圈很淡的光晕；虚线朝未来的方向缓缓流动。进入时从第一站到最后一站一段段画出来。
 * WebGL 的 THREE.Line 只有 1 像素宽，这里用 three.js 自带的 Line2（三角形条带画的粗线，同一个包，不算新依赖）。
 */
import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';

const GOLD = 0xf3cf8b;
/** 虚线一段多长、隔多长（世界单位），往未来流多快（单位 / 秒） */
const DASH = 0.5, GAP = 0.32, FLOW = 0.25;

function material(width: number, dashed: boolean): LineMaterial {
  const m = new LineMaterial({ color: GOLD, linewidth: width, dashed, dashSize: DASH, gapSize: GAP, transparent: true, opacity: 0 });
  m.depthTest = false;
  m.depthWrite = false;
  m.blending = THREE.AdditiveBlending;
  m.toneMapped = false;
  return m;
}

export class ConstellationLines {
  readonly group = new THREE.Group();
  private readonly mainMat = material(1.4, true);
  private readonly glowMat = material(5, false);
  private main: Line2 | null = null;
  private glow: Line2 | null = null;
  private segments = 0;

  constructor() {
    this.group.renderOrder = 3;
  }

  /** 各站的位置（按时间先后）；少于两站时没有线 */
  set(points: readonly THREE.Vector3[]): void {
    this.clear();
    this.segments = Math.max(0, points.length - 1);
    if (!this.segments) return;
    const flat = points.flatMap((p) => [p.x, p.y, p.z]);
    const make = (mat: LineMaterial) => {
      const geo = new LineGeometry();
      geo.setPositions(flat);
      const line = new Line2(geo, mat);
      line.computeLineDistances();
      line.frustumCulled = false;
      line.renderOrder = 3;
      this.group.add(line);
      return line;
    };
    this.glow = make(this.glowMat);
    this.main = make(this.mainMat);
  }

  /** 线宽按 CSS 像素算：resolution 用画布的 CSS 尺寸 */
  setResolution(width: number, height: number): void {
    this.mainMat.resolution.set(width, height);
    this.glowMat.resolution.set(width, height);
  }

  /**
   * opacity 0–1；reveal 0–1 是画出来了多少段（进入时从第一站往最后一站画）；flow 是虚线往未来流动了多远（世界单位）
   */
  update(opacity: number, reveal: number, flow: number): void {
    this.group.visible = opacity > 0.002 && this.segments > 0;
    if (!this.group.visible) return;
    this.mainMat.opacity = 0.8 * opacity;
    this.glowMat.opacity = 0.08 * opacity;
    // 虚线的花纹往线的起点那边挪，看起来就是往未来流
    this.mainMat.dashOffset = -flow;
    const shown = Math.max(1, Math.ceil(reveal * this.segments));
    for (const line of [this.main, this.glow]) if (line) line.geometry.instanceCount = shown;
  }

  /** 流动的速度（单位 / 秒） */
  static readonly flowSpeed = FLOW;

  private clear(): void {
    for (const line of [this.main, this.glow]) {
      if (!line) continue;
      this.group.remove(line);
      line.geometry.dispose();
    }
    this.main = this.glow = null;
    this.segments = 0;
  }
}

const RING_VERT = /* glsl */ `
attribute vec3 aPos;
attribute float aRadius;
attribute float aFirst;
attribute float aCurrent;
uniform float uViewH;
uniform float uMinPx;
varying vec2 vUv;
varying float vFirst;
varying float vCurrent;

void main() {
  vec4 mv = modelViewMatrix * vec4(aPos, 1.0);
  float pxPerUnit = uViewH * 0.5 * projectionMatrix[1][1] / max(-mv.z, 0.001);
  // 面片比环大两成（外面那圈「当前站」的环、光晕要地方）；离得远时环至少 uMinPx 像素
  float size = max(aRadius * 2.4, uMinPx * 1.2 / pxPerUnit);
  mv.xy += position.xy * size;
  gl_Position = projectionMatrix * mv;
  vUv = position.xy * 2.0;
  vFirst = aFirst;
  vCurrent = aCurrent;
}
`;

const RING_FRAG = /* glsl */ `
uniform float uTime;
uniform float uOpacity;
varying vec2 vUv;
varying float vFirst;
varying float vCurrent;

void main() {
  float r = length(vUv);
  if (r >= 1.0) discard;
  vec3 gold = vec3(1.0, 0.82, 0.55);
  // 细环在 1/1.2 处，慢慢呼吸；当前站更亮、外面再多一圈；第一站的环里多一颗小核，表示「从这天开始」
  float breathe = 0.5 + 0.15 * (0.5 + 0.5 * sin(uTime * 1.3));
  float ring = exp(-pow((r - 0.833) / 0.028, 2.0)) * mix(breathe, 1.0, vCurrent);
  float outer = vCurrent * 0.55 * exp(-pow((r - 0.95) / 0.02, 2.0));
  float core = vFirst * exp(-r * r * 90.0) * 0.9;
  gl_FragColor = vec4(gold * (ring + outer + core) * uOpacity, 1.0);
  #include <colorspace_fragment>
}
`;

/** 一站的环：在那天星团的中心，半径比星团大一点；first 是第一站，current 是现在看的那一站 */
export interface RingInit {
  x: number;
  y: number;
  z: number;
  radius: number;
  first: boolean;
  current: boolean;
}

/**
 * 每一站的细金环（design §11.10 第二期）：一站一个面片，一次画完。星团的大小随那天的照片数（向日葵铺开，半径 0.17·√n），
 * 环套在外面一点；离得远时至少十几个像素，总览里也看得出哪几天是站
 */
export class StationRings {
  readonly mesh: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.ShaderMaterial>;
  readonly uniforms = {
    uTime: { value: 0 },
    uViewH: { value: 800 },
    uMinPx: { value: 12 },
    uOpacity: { value: 0 },
  };

  constructor() {
    const material = new THREE.ShaderMaterial({
      vertexShader: RING_VERT,
      fragmentShader: RING_FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(this.build([]), material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 3;
  }

  private build(list: readonly RingInit[]): THREE.InstancedBufferGeometry {
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]), 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const n = Math.max(1, list.length);
    const pos = new Float32Array(n * 3), radius = new Float32Array(n), first = new Float32Array(n), current = new Float32Array(n);
    list.forEach((r, k) => {
      pos.set([r.x, r.y, r.z], k * 3);
      radius[k] = r.radius;
      first[k] = r.first ? 1 : 0;
      current[k] = r.current ? 1 : 0;
    });
    geo.setAttribute('aPos', new THREE.InstancedBufferAttribute(pos, 3));
    geo.setAttribute('aRadius', new THREE.InstancedBufferAttribute(radius, 1));
    geo.setAttribute('aFirst', new THREE.InstancedBufferAttribute(first, 1));
    geo.setAttribute('aCurrent', new THREE.InstancedBufferAttribute(current, 1));
    geo.instanceCount = list.length;
    return geo;
  }

  /** 整个换掉（站不多，每次重建） */
  set(list: readonly RingInit[]): void {
    const old = this.mesh.geometry;
    this.mesh.geometry = this.build(list);
    old.dispose();
  }
}
